import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { getPublicSiteUrl } from '@/lib/seo';
import { Search, Car, Building2, Package, Filter, MapPin, Calendar, Clock } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ProductCard } from '@/components/products/ProductCard';
import { useCurrency } from '@/lib/currency-context';
import { buildPathForLocale } from '@/lib/intl-seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SearchPage = () => {
  const { lang } = useParams();
  const { t, i18n } = useTranslation();
  const site = getPublicSiteUrl();
  const langCode = lang || i18n.language || 'tr';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { convertPrice, formatPrice } = useCurrency();
  
  const query = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(query);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  
  const [products, setProducts] = useState([]);
  const [cars, setCars] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [flights, setFlights] = useState([]);

  useEffect(() => {
    if (query) {
      fetchAllResults(query);
    } else {
      setLoading(false);
    }
  }, [query, i18n.language]);

  const fetchAllResults = async (searchTerm) => {
    setLoading(true);
    try {
      const lang = i18n.language;
      
      // Fetch all in parallel
      const [productsRes, carsRes, hotelsRes, flightsRes] = await Promise.all([
        fetch(`${API_URL}/api/products?search=${encodeURIComponent(searchTerm)}&lang=${lang}&limit=20`),
        fetch(`${API_URL}/api/car-rentals?search=${encodeURIComponent(searchTerm)}&limit=20`),
        fetch(`${API_URL}/api/hotel-flights/hotels?search=${encodeURIComponent(searchTerm)}&limit=20`),
        fetch(`${API_URL}/api/hotel-flights/flights?search=${encodeURIComponent(searchTerm)}&limit=20`)
      ]);

      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data.products || []);
      }
      
      if (carsRes.ok) {
        const data = await carsRes.json();
        setCars(data.cars || []);
      }
      
      if (hotelsRes.ok) {
        const data = await hotelsRes.json();
        setHotels(data.hotels || []);
      }
      
      if (flightsRes.ok) {
        const data = await flightsRes.json();
        setFlights(data.flights || []);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery });
    }
  };

  const handleAiSearch = () => {
    const q = searchQuery.trim();
    if (q.length < 2) return;
    const base = buildPathForLocale(langCode, ['ai']);
    navigate(q.length >= 2 ? `${base}?q=${encodeURIComponent(q)}` : base);
  };

  const totalResults = products.length + cars.length + hotels.length + flights.length;

  const getCarImage = (car) => {
    if (car.images && car.images.length > 0) return car.images[0];
    return 'https://images.unsplash.com/photo-1485291571150-772bcfc10da5?w=400';
  };

  const getHotelImage = (hotel) => {
    if (hotel.images && hotel.images.length > 0) return hotel.images[0];
    return 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400';
  };

  return (
    <div className="min-h-screen bg-background">
      {query ? (
        <Helmet htmlAttributes={{ lang: langCode }}>
          <meta name="robots" content="noindex,follow" />
          {site ? <link rel="canonical" href={`${site}/${langCode}/search`} /> : null}
        </Helmet>
      ) : null}
      {/* Search Header */}
      <section className="bg-gradient-to-b from-primary/5 to-transparent py-8">
        <div className="container mx-auto px-4">
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('search.placeholder')}
                  className="pl-10 h-12 text-lg"
                />
              </div>
              <Button type="submit" size="lg" className="px-8">
                {t('search.search')}
              </Button>
              <Button
                type="button"
                size="lg"
                variant="outline"
                onClick={handleAiSearch}
                disabled={searchQuery.trim().length < 2}
                data-testid="ai-search-btn-page"
                className="px-4 border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                {t('search.aiButton', 'AI Modu')}
              </Button>
            </div>
          </form>
          
          {query && (
            <div className="text-center mt-4">
              <p className="text-muted-foreground">
                {t('search.resultsFor')} &quot;<span className="font-semibold text-foreground">{query}</span>&quot;
                {!loading && ` - ${totalResults} ${t('search.resultsFound')}`}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <Card key={i}>
                <Skeleton className="h-48 w-full" />
                <CardContent className="p-4 space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : !query ? (
          <div className="text-center py-16">
            <Search className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-2xl font-semibold mb-2">{t('search.enterQuery')}</h2>
            <p className="text-muted-foreground">{t('search.searchDescription')}</p>
          </div>
        ) : totalResults === 0 ? (
          <div className="text-center py-16">
            <Search className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-2xl font-semibold mb-2">{t('search.noResults')}</h2>
            <p className="text-muted-foreground">{t('search.tryDifferent')}</p>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="all" className="gap-2">
                {t('search.all')} ({totalResults})
              </TabsTrigger>
              {products.length > 0 && (
                <TabsTrigger value="products" className="gap-2">
                  <Package className="w-4 h-4" />
                  {t('search.products')} ({products.length})
                </TabsTrigger>
              )}
              {cars.length > 0 && (
                <TabsTrigger value="cars" className="gap-2">
                  <Car className="w-4 h-4" />
                  {t('search.cars')} ({cars.length})
                </TabsTrigger>
              )}
              {hotels.length > 0 && (
                <TabsTrigger value="hotels" className="gap-2">
                  <Building2 className="w-4 h-4" />
                  {t('search.hotels')} ({hotels.length})
                </TabsTrigger>
              )}
            </TabsList>

            {/* All Results */}
            <TabsContent value="all">
              <div className="space-y-8">
                {/* Products */}
                {products.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      {t('search.products')} ({products.length})
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {products.slice(0, 4).map((product) => (
                        <ProductCard key={product.id} product={product} />
                      ))}
                    </div>
                    {products.length > 4 && (
                      <div className="text-center mt-4">
                        <Button variant="outline" onClick={() => setActiveTab('products')}>
                          {t('search.viewAll')} ({products.length})
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Cars */}
                {cars.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Car className="w-5 h-5" />
                      {t('search.cars')} ({cars.length})
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {cars.slice(0, 4).map((car) => (
                        <Card key={car.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                          <div className="relative h-40">
                            <img src={getCarImage(car)} alt={car.title} className="w-full h-full object-cover" />
                            <Badge className="absolute top-2 left-2">{car.category || 'sedan'}</Badge>
                          </div>
                          <CardContent className="p-4">
                            <h4 className="font-semibold">{car.title}</h4>
                            <p className="text-sm text-muted-foreground">{car.brand} {car.model}</p>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                              <MapPin className="w-3 h-3" />
                              {car.location || car.pickup_location?.city}
                            </div>
                            {car.pricing?.daily && (
                              <p className="text-primary font-bold mt-2">
                                {formatPrice(convertPrice(car.pricing.daily, car.currency))}/{t('carRental.day')}
                              </p>
                            )}
                          </CardContent>
                          <CardFooter className="p-4 pt-0">
                            <Button asChild className="w-full" size="sm">
                              <Link to="/car-rentals">{t('search.viewDetails')}</Link>
                            </Button>
                          </CardFooter>
                        </Card>
                      ))}
                    </div>
                    {cars.length > 4 && (
                      <div className="text-center mt-4">
                        <Button variant="outline" onClick={() => setActiveTab('cars')}>
                          {t('search.viewAll')} ({cars.length})
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Hotels */}
                {hotels.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      {t('search.hotels')} ({hotels.length})
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {hotels.slice(0, 4).map((hotel) => (
                        <Card key={hotel.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                          <div className="relative h-40">
                            <img src={getHotelImage(hotel)} alt={hotel.name} className="w-full h-full object-cover" />
                          </div>
                          <CardContent className="p-4">
                            <h4 className="font-semibold">{hotel.name}</h4>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                              <MapPin className="w-3 h-3" />
                              {hotel.location || hotel.city}
                            </div>
                            {hotel.price_per_night && (
                              <p className="text-primary font-bold mt-2">
                                {formatPrice(convertPrice(hotel.price_per_night, hotel.currency))}/{t('hotelFlight.perNight')}
                              </p>
                            )}
                          </CardContent>
                          <CardFooter className="p-4 pt-0">
                            <Button asChild className="w-full" size="sm">
                              <Link to="/hotel-flights">{t('search.viewDetails')}</Link>
                            </Button>
                          </CardFooter>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Products Tab */}
            <TabsContent value="products">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </TabsContent>

            {/* Cars Tab */}
            <TabsContent value="cars">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cars.map((car) => (
                  <Card key={car.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="relative h-48">
                      <img src={getCarImage(car)} alt={car.title} className="w-full h-full object-cover" />
                      <Badge className="absolute top-3 left-3">{car.category || 'sedan'}</Badge>
                    </div>
                    <CardContent className="p-4">
                      <h4 className="font-bold text-lg">{car.title}</h4>
                      <p className="text-muted-foreground">{car.brand} {car.model} {car.year && `• ${car.year}`}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                        <MapPin className="w-4 h-4" />
                        {car.location || car.pickup_location?.city}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {car.pricing?.hourly && (
                          <Badge variant="outline">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatPrice(convertPrice(car.pricing.hourly, car.currency))}/{t('carRental.hour')}
                          </Badge>
                        )}
                        {car.pricing?.daily && (
                          <Badge variant="secondary">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatPrice(convertPrice(car.pricing.daily, car.currency))}/{t('carRental.day')}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                    <CardFooter className="p-4 pt-0">
                      <Button asChild className="w-full">
                        <Link to="/car-rentals">{t('carRental.bookNow')}</Link>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Hotels Tab */}
            <TabsContent value="hotels">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {hotels.map((hotel) => (
                  <Card key={hotel.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="relative h-48">
                      <img src={getHotelImage(hotel)} alt={hotel.name} className="w-full h-full object-cover" />
                    </div>
                    <CardContent className="p-4">
                      <h4 className="font-bold text-lg">{hotel.name}</h4>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <MapPin className="w-4 h-4" />
                        {hotel.location || hotel.city}
                      </div>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{hotel.description}</p>
                      {hotel.price_per_night && (
                        <p className="text-primary font-bold text-lg mt-3">
                          {formatPrice(convertPrice(hotel.price_per_night, hotel.currency))}/{t('hotelFlight.perNight')}
                        </p>
                      )}
                    </CardContent>
                    <CardFooter className="p-4 pt-0">
                      <Button asChild className="w-full">
                        <Link to="/hotel-flights">{t('hotelFlight.bookNow')}</Link>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </section>
    </div>
  );
};

export default SearchPage;
